import { useMemo } from "react";
import { CheckCircle, Clock } from "@phosphor-icons/react";
import { formatPrice } from "./format";
import { useLocale } from "@/i18n";

const CATEGORY_ORDER_PT = ["Cortes", "Barbas", "Sobrancelhas", "Combos", "Outros"];
const CATEGORY_ORDER_ES = ["Cortes", "Barbas", "Cejas", "Combos", "Otros"];

export default function ServiceStep({ services, selected, onSelect }) {
  const { t, locale } = useLocale();

  const groups = useMemo(() => {
    const map = {};
    for (const s of services) {
      (map[s.category] = map[s.category] || []).push(s);
    }
    const order = locale === "es" ? CATEGORY_ORDER_ES : CATEGORY_ORDER_PT;
    const known = order.filter((c) => map[c]);
    const extra = Object.keys(map).filter((c) => !order.includes(c));
    return [...known, ...extra].map((c) => ({ category: c, items: map[c] }));
  }, [services, locale]);

  return (
    <section data-testid="service-step">
      <p className="text-xs uppercase tracking-[0.2em] font-semibold text-amber-500">{t("step1")}</p>
      <h2 className="text-2xl font-bold tracking-tight text-zinc-100 mt-1">{t("choose_service")}</h2>

      {groups.length === 0 ? (
        <p className="mt-5 text-sm text-zinc-400 bg-zinc-900 border border-zinc-800 rounded-xl p-4" data-testid="no-services-public">
          {t("no_services_public")}
        </p>
      ) : groups.map((g) => (
        <div key={g.category} className="mt-6">
          <h3 className="text-xs uppercase tracking-[0.2em] font-semibold text-zinc-500 mb-3">
            {g.category}
          </h3>
          <div className="space-y-3">
            {g.items.map((s, i) => {
              const active = selected?.id === s.id;
              return (
                <button
                  key={s.id}
                  data-testid={`service-card-${s.id}`}
                  onClick={() => onSelect(s)}
                  style={{ animationDelay: `${i * 40}ms` }}
                  className={`animate-fade-up w-full text-left flex items-center justify-between gap-4 p-4 rounded-xl border transition-colors duration-200 ${
                    active
                      ? "border-amber-500 bg-zinc-800/50"
                      : "border-zinc-800 bg-zinc-900 hover:border-zinc-700"
                  }`}
                >
                  <div className="min-w-0">
                    <p className="font-bold text-zinc-100">{s.name}</p>
                    <p className="flex items-center gap-1 text-xs text-zinc-400 mt-1.5">
                      <Clock size={13} weight="duotone" className="text-amber-500" />
                      {s.duration_min} min
                    </p>
                  </div>
                  <div className="flex flex-col items-end gap-2 shrink-0">
                    <span className="text-lg font-bold text-amber-500">{formatPrice(s.price, locale)}</span>
                    <CheckCircle
                      size={22}
                      weight={active ? "fill" : "regular"}
                      className={active ? "text-amber-500" : "text-zinc-700"}
                    />
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </section>
  );
}
